import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { UserType } from '@api/Users/User';
import UserService from '@api/Users/UserCtrl';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';

interface ModalChangeEmailProps {
    open: boolean;
    onClose: () => void;
    user: Pick<UserType, 'uid' | 'email' | 'profile'>;
    /** El correo como quedó (normalizado por el server). */
    onChanged: (email: string) => void;
}

/**
 * Cambiar el correo con el que entra un usuario (plan de Gastos §8.3): quien se dio
 * de alta con el correo interno `@staff.krispis.mx` pone el suyo cuando lo tenga.
 * El PIN no cambia.
 */
export const ModalChangeEmail: React.FC<ModalChangeEmailProps> = ({ open, onClose, user, onChanged }) => {
    const userService = useMemo(() => new UserService(), []);
    const [email, setEmail] = useState('');

    useEffect(() => {
        if (open) setEmail('');
    }, [open]);

    const onSave = async (event: React.FormEvent) => {
        event.preventDefault();
        showLoader();
        try {
            const saved = await userService.onUpdateEmail(user, email);
            showAlertMessage(`Listo: desde ahora entra con ${saved}`);
            onChanged(saved);
            onClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15 } }}>
            <form onSubmit={onSave}>
                <DialogTitle fontWeight={600}>Cambiar correo</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2" color="text.secondary">
                            {`Hoy entra con ${user.email}. El PIN sigue siendo el mismo.`}
                        </Typography>
                        <TextField
                            autoFocus
                            required
                            fullWidth
                            type="email"
                            label="Correo nuevo"
                            autoComplete="off"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={!email.trim()}>
                        Guardar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
